import { useContext, useEffect, useState } from "react";
import { Form, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faComments } from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";
import GroupPostCommentForm from "@serie3/mymediaplanner/Modules/Groups/Forms/GroupPostCommentForm";
import SettingsContext from "@serie3/common/auth/SettingsContext";

const formatDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d)) return null;
  return (
    d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) +
    " · " +
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
  );
};

const Avatar = ({ name }) => {
  const initials = name
    ? name.trim().split(/\s+/).slice(0, 2).map((w) => w[0].toUpperCase()).join("")
    : "?";
  return (
    <div style={{
      width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
      background: "linear-gradient(135deg, #564ab1, #3d3490)",
      color: "#fff", fontSize: 11, fontWeight: 700,
      display: "flex", alignItems: "center", justifyContent: "center",
      letterSpacing: 0.5,
    }}>
      {initials}
    </div>
  );
};

const GroupCommentsTab = ({ share, group }) => {
  const { t } = useTranslation();
  const { settings } = useContext(SettingsContext);
  const currentUser = settings?.user?.username;

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(0);

  useEffect(() => {
    if (!share?.id || !group?.id) return;
    setLoading(true);
    setComments([]);
    crudApi
      .justGet(MediaplannerGroupEndpoints.endpoints.listCommentsOnPost + group.id + "/" + share.id)
      .then((r) => { setComments(r.ok && Array.isArray(r.data?.comments) ? r.data.comments : []); })
      .finally(() => setLoading(false));
  }, [share?.id, group?.id]);

  const commentForm = GroupPostCommentForm.createGroupPostCommentForm(
    comments,
    setComments,
    group?.id,
    share?.id,
    setEditingId,
  );
  const formik = useFormik(commentForm);

  const deleteComment = async (commentId) => {
    const response = await crudApi.del(
      MediaplannerGroupEndpoints.endpoints.delete + group.id + "/",
      commentId,
    );
    if (response.status === 200) {
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    }
  };

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", paddingBottom: 32 }}>

      {/* Header banner — makes clear these are NOT LinkedIn comments */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        background: "linear-gradient(135deg, rgba(86,74,177,0.08), rgba(61,52,144,0.04))",
        border: "1px solid rgba(86,74,177,0.2)",
        borderRadius: 10, padding: "10px 16px", marginBottom: 24,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, flexShrink: 0,
          background: "linear-gradient(135deg, #564ab1, #3d3490)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <FontAwesomeIcon icon={faComments} style={{ color: "#fff", fontSize: 14 }} />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#564ab1" }}>
            {t("Backend.Groups.Comments.InToolTitle")}
          </div>
          <div style={{ fontSize: 11, color: "#7c6fc4", marginTop: 1 }}>
            {t("Backend.Groups.Comments.InToolSubtitle")}
          </div>
        </div>
        {comments.length > 0 && (
          <span style={{
            marginLeft: "auto", background: "rgba(86,74,177,0.12)",
            color: "#564ab1", fontSize: 11, fontWeight: 700,
            borderRadius: 20, padding: "2px 10px",
          }}>
            {comments.length}
          </span>
        )}
      </div>

      {/* Comment list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 24 }}>
        {loading && [1, 2, 3].map((i) => (
          <div key={i} style={{ display: "flex", gap: 10 }}>
            <div className="skeleton" style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0 }} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <div className="skeleton" style={{ height: 11, width: "22%", borderRadius: 6 }} />
                <div className="skeleton" style={{ height: 10, width: "16%", borderRadius: 6, opacity: 0.6 }} />
              </div>
              <div className="skeleton" style={{ height: 11, width: "75%", borderRadius: 6 }} />
              <div className="skeleton" style={{ height: 11, width: "50%", borderRadius: 6 }} />
            </div>
          </div>
        ))}

        {!loading && comments.length === 0 && (
          <div style={{
            textAlign: "center", padding: "32px 0",
            color: "#9299a8", fontSize: 13,
          }}>
            {t("Backend.Groups.Post.NoComments")}
          </div>
        )}

        {!loading && comments.map((c) => {
          const dateStr = formatDate(c.last_edit ?? null);
          const isOwn = c.user?.username === currentUser;
          return (
            <div key={c.id} style={{ display: "flex", gap: 10 }}>
              <Avatar name={c.user?.username} />
              <div style={{
                flex: 1,
                background: isOwn ? "rgba(86,74,177,0.05)" : "#f8f9fc",
                borderRadius: isOwn ? "12px 4px 12px 12px" : "4px 12px 12px 12px",
                padding: "10px 14px",
                border: isOwn ? "1px solid rgba(86,74,177,0.2)" : "1px solid #eaecf0",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1a1f2e" }}>
                    {c.user?.username ?? ""}
                  </span>
                  {dateStr && (
                    <span style={{ fontSize: 11, color: "#9299a8" }}>{dateStr}</span>
                  )}
                  {isOwn && (
                    <button
                      type="button"
                      onClick={() => deleteComment(c.id)}
                      title={t("Common.Delete")}
                      style={{
                        marginLeft: "auto", background: "none", border: "none",
                        padding: "2px 4px", cursor: "pointer", color: "#cdd0d6",
                        fontSize: 11, lineHeight: 1, transition: "color 0.15s ease",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = "#e74c3c"}
                      onMouseLeave={(e) => e.currentTarget.style.color = "#cdd0d6"}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
                <p style={{ fontSize: 13, color: "#374151", margin: 0, lineHeight: 1.6 }}>
                  {c.text}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* New comment */}
      <Form onSubmit={formik.handleSubmit}>
        <div style={{
          display: "flex", gap: 10, alignItems: "flex-end",
          background: "#f8f9fc", borderRadius: 12,
          border: "1px solid rgba(86,74,177,0.2)", padding: "10px 12px",
        }}>
          <Avatar name={currentUser} />
          <Form.Control
            as="textarea"
            rows={2}
            name="text"
            value={formik.values.text}
            onChange={formik.handleChange}
            placeholder={t("Backend.Groups.Post.CommentPlaceholder")}
            style={{
              flex: 1, border: "none", background: "transparent",
              resize: "none", fontSize: 13, padding: 0,
              boxShadow: "none", outline: "none",
            }}
          />
          <Button
            type="submit"
            disabled={formik.isSubmitting || !formik.values.text}
            style={{
              padding: "7px 18px", borderRadius: 20, border: "none",
              background: formik.values.text
                ? "linear-gradient(135deg, #564ab1, #3d3490)"
                : "#e0e3ea",
              color: formik.values.text ? "#fff" : "#9299a8",
              fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
              cursor: formik.values.text ? "pointer" : "default",
              transition: "background 0.15s ease, color 0.15s ease",
              flexShrink: 0,
            }}
          >
            {t("Backend.Groups.Post.AddComment")}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default GroupCommentsTab;
